// iter160 — "Confirm it's you" step before linking an existing account to the Toolkit.
import { useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/lib/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ERRORS = {
  phone_mismatch: "This is not the mobile number from Teacher Utility. Use the same number.",
  bad_password: "Wrong password. Please try again.",
  ticket_invalid: "This confirmation has expired. Please tap again in Teacher Utility.",
  rate_limited: "Too many attempts. Please wait a minute.",
};

export const SsoConfirm = ({ ticket, phoneLast4 }) => {
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const { adoptSession } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await axios.post(`${API}/sso/toolkit/confirm`, { ticket, mobile, password });
      try { sessionStorage.setItem("mdm.ssoArrival", "1"); } catch { /* ignore */ }
      adoptSession(data.token, data.user);
      navigate(data.redirect === "/dashboard" ? "/dashboard" : "/choose-plan", { replace: true });
    } catch (err) {
      setError(ERRORS[err?.response?.data?.error] || "Could not confirm. Please try again.");
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="w-full max-w-sm rounded-2xl border border-border bg-white p-6 shadow-sm" data-testid="sso-confirm-form">
      <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-moss/15 text-moss">
        <ShieldCheck className="h-6 w-6" />
      </div>
      <h1 className="text-center font-heading text-lg font-bold text-ink-900" data-testid="sso-confirm-title">Confirm it's you</h1>
      <p className="mt-1 text-center text-xs text-ink-700">
        One-time check for the MDM Sahayak account on ••{phoneLast4}. Next time Teacher Utility will log you in directly.
      </p>
      <Input className="mt-4" inputMode="numeric" maxLength={10} placeholder="Mobile number" value={mobile}
        onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))} data-testid="sso-confirm-mobile" required />
      <Input className="mt-2" type="password" placeholder="MDM Sahayak password" value={password}
        onChange={(e) => setPassword(e.target.value)} data-testid="sso-confirm-password" required />
      {error && <p className="mt-2 text-xs font-medium text-rose-700" data-testid="sso-confirm-error">{error}</p>}
      <Button type="submit" className="mt-4 w-full" disabled={busy} data-testid="sso-confirm-submit">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm & continue"}
      </Button>
      <Link to="/login" className="mt-3 block text-center text-xs text-ink-700 underline" data-testid="sso-confirm-login-link">Log in normally instead</Link>
    </form>
  );
};

export const SsoNoPassword = ({ phoneLast4 }) => (
  <div className="w-full max-w-sm rounded-2xl border border-border bg-white p-6 text-center shadow-sm" data-testid="sso-no-password">
    <h1 className="font-heading text-lg font-bold text-ink-900">Confirm it's you</h1>
    <p className="mt-2 text-sm text-ink-700">
      The account on ••{phoneLast4} has no password yet. Open the login page and tap "Forgot password" to get one, then tap again in Teacher Utility.
    </p>
    <Button asChild className="mt-5 w-full" data-testid="sso-no-password-login-btn"><Link to="/login">Go to login</Link></Button>
  </div>
);
